import { api, desenvolver } from '../api';
import type { CrearUsuarioRequest, ModificarUsuarioRequest, Rol, Usuario } from '../api';

/**
 * Las seis llamadas de `/api/v1/admin/usuarios`, y nada más (KAN-143).
 *
 * Está separado de los componentes por la misma razón que `sesion/autenticacion.ts`
 * lo está del almacén: acá vive el trato con la red, allá el comportamiento de la
 * pantalla, y cada uno se puede leer —y probar— sin el otro.
 *
 * **Ningún tipo se escribe acá.** `Usuario`, `CrearUsuarioRequest` y
 * `ModificarUsuarioRequest` son alias de `schema.d.ts`, que sale de
 * `docs/openapi.yaml`. Si el backend renombra un campo, esto deja de compilar.
 */

/** `GET`. Devuelve **activos e inactivos**: la baja es lógica y hay que verla. */
export function listarUsuarios(): Promise<Usuario[]> {
  return desenvolver(api.GET('/api/v1/admin/usuarios'));
}

/**
 * `POST`. **Sin `rol` el backend crea `OPERADOR`**, que es el menor privilegio.
 * Quien llama manda `rol` solo si la persona lo eligió a propósito: ver
 * {@link altaDe}.
 */
export function crearUsuario(pedido: CrearUsuarioRequest): Promise<Usuario> {
  return desenvolver(api.POST('/api/v1/admin/usuarios', { body: pedido }));
}

/**
 * `PATCH`. **Parcial**: se manda solo lo que cambió (ver {@link cambiosDe}), no
 * el objeto entero. Mandar el objeto completo convertiría cada edición en una
 * escritura de todos los campos y pisaría, sin que nadie lo pidiera, lo que otro
 * administrador acabara de cambiar en la misma fila.
 */
export function modificarUsuario(id: number, cambios: ModificarUsuarioRequest): Promise<Usuario> {
  return desenvolver(
    api.PATCH('/api/v1/admin/usuarios/{id}', { params: { path: { id } }, body: cambios }),
  );
}

/** `POST /{id}/password`. No pide la contraseña actual: es una operación de admin. */
export function cambiarPassword(id: number, password: string): Promise<Usuario> {
  return desenvolver(
    api.POST('/api/v1/admin/usuarios/{id}/password', {
      params: { path: { id } },
      body: { password },
    }),
  );
}

/**
 * `DELETE`. **Baja lógica, no borrado**: la fila queda para poder atribuir un
 * comprobante a quien lo pidió. Por eso la función se llama `desactivar` y no
 * `borrar`, igual que el botón de la pantalla.
 */
export function desactivarUsuario(id: number): Promise<Usuario> {
  return desenvolver(api.DELETE('/api/v1/admin/usuarios/{id}', { params: { path: { id } } }));
}

/** `POST /{id}/activar`. Revierte la baja lógica; es idempotente. */
export function activarUsuario(id: number): Promise<Usuario> {
  return desenvolver(
    api.POST('/api/v1/admin/usuarios/{id}/activar', { params: { path: { id } } }),
  );
}

/**
 * Arma el cuerpo del `PATCH` con **lo que la persona tocó y nada más**.
 * Devuelve `null` si no cambió nada, para no gastar un pedido.
 *
 * Es una función aparte y no unas líneas dentro del componente porque es
 * exactamente la regla que la tarjeta pide no perder, y así se puede fijar con
 * un test sin montar media pantalla.
 */
export function cambiosDe(
  original: Pick<Usuario, 'nombre' | 'rol'>,
  editado: { nombre: string; rol: Rol },
): ModificarUsuarioRequest | null {
  const cambios: ModificarUsuarioRequest = {};
  if (editado.nombre !== original.nombre) {
    cambios.nombre = editado.nombre;
  }
  if (editado.rol !== original.rol) {
    cambios.rol = editado.rol;
  }
  return Object.keys(cambios).length === 0 ? null : cambios;
}

/**
 * Arma el cuerpo del alta. **El `rol` viaja siempre** porque el formulario
 * siempre muestra uno elegido —y arranca en `OPERADOR`—, así que no hay campo
 * "sin decidir" que dejar afuera. Lo que la UI respeta es el otro lado de la
 * regla: **no preselecciona ADMIN**.
 */
export function altaDe(datos: {
  nombre: string;
  email: string;
  password: string;
  rol: Rol;
}): CrearUsuarioRequest {
  return {
    nombre: datos.nombre.trim(),
    email: datos.email.trim(),
    password: datos.password,
    rol: datos.rol,
  };
}
